import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Capture } from '../../../core/models/capture.model';
import { CaptureHistoryItem } from '../../../core/models/capture-history-item.model';
import { CaptureService } from '../../../core/services/capture.service';
import { CaptureHistoryItemActions } from '../../../core/enums/capture-history-item-actions.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { DatePipe } from '@angular/common';
import { CaptureHeaderComponent } from '../../components/capture-header/capture-header.component';
import { CaptureHistoryItemTypeCreatedComponent } from '../../components/capture-history-item-type-created/capture-history-item-type-created.component';
import { CaptureHistoryItemTypeUpdatedComponent } from '../../components/capture-history-item-type-updated/capture-history-item-type-updated.component';
import { CaptureHistoryItemTypeTransitionedComponent } from '../../components/capture-history-item-type-transitioned/capture-history-item-type-transitioned.component';
import { CaptureHistoryItemTypeReprocessingStartedComponent } from '../../components/capture-history-item-type-reprocessing-started/capture-history-item-type-reprocessing-started.component';
import { CaptureHistoryItemTypeUnreversedComponent } from '../../components/capture-history-item-type-unreversed/capture-history-item-type-unreversed.component';
import { CaptureHistoryItemTypeSnapshotCreatedComponent } from '../../components/capture-history-item-type-snapshot-created/capture-history-item-type-snapshot-created.component';
import { CaptureHistoryItemTypeVerifyingPatientAttachmentAssociatedComponent } from '../../components/capture-history-item-type-verifying-patient-attachment-associated/capture-history-item-type-verifying-patient-attachment-associated.component';
import { CaptureHistoryItemTypeConsultNoteSubmittedComponent } from '../../components/capture-history-item-type-consult-note-submitted/capture-history-item-type-consult-note-submitted.component';
import { CaptureHistoryItemTypeReferralMatchVerificationSubmittedComponent } from '../../components/capture-history-item-type-referral-match-verification-submitted/capture-history-item-type-referral-match-verification-submitted.component';
import { CaptureHistoryItemTypeBaseComponent } from '../../components/capture-history-item-type-base/capture-history-item-type-base.component';
import { CaptureHistoryItemTypeLocationAddedComponent } from '../../components/capture-history-item-type-location-added/capture-history-item-type-location-added.component';
import { CaptureHistoryItemTypeDeveloperChoreUpdatedComponent } from '../../components/capture-history-item-type-developer-chore-updated/capture-history-item-type-developer-chore-updated.component';
import { CaptureHistoryItemTypeClientChoreStartedComponent } from '../../components/capture-history-item-type-client-chore-started/capture-history-item-type-client-chore-started.component';
import { CaptureHistoryItemTypeClientChoreUpdatedComponent } from '../../components/capture-history-item-type-client-chore-updated/capture-history-item-type-client-chore-updated.component';
import { CaptureHistoryItemTypeClaimStatusUpdatedComponent } from '../../components/capture-history-item-type-claim-status-updated/capture-history-item-type-claim-status-updated.component';
import { CaptureHistoryItemTypeClientNoteAddedComponent } from '../../components/capture-history-item-type-client-note-added/capture-history-item-type-client-note-added.component';
import { CaptureHistoryItemTypeClientNoteRemovedComponent } from '../../components/capture-history-item-type-client-note-removed/capture-history-item-type-client-note-removed.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-capture-history-new',
    templateUrl: './capture-history-new.component.html',
    styleUrls: ['./capture-history-new.component.scss'],
    imports: [CaptureHeaderComponent,
              CaptureHistoryItemTypeCreatedComponent,
              CaptureHistoryItemTypeUpdatedComponent,
              CaptureHistoryItemTypeTransitionedComponent,
              CaptureHistoryItemTypeReprocessingStartedComponent,
              CaptureHistoryItemTypeUnreversedComponent,
              CaptureHistoryItemTypeSnapshotCreatedComponent,
              CaptureHistoryItemTypeVerifyingPatientAttachmentAssociatedComponent,
              CaptureHistoryItemTypeConsultNoteSubmittedComponent,
              CaptureHistoryItemTypeReferralMatchVerificationSubmittedComponent,
              CaptureHistoryItemTypeBaseComponent,
              CaptureHistoryItemTypeLocationAddedComponent,
              CaptureHistoryItemTypeDeveloperChoreUpdatedComponent,
              CaptureHistoryItemTypeClientChoreStartedComponent,
              CaptureHistoryItemTypeClientChoreUpdatedComponent,
              CaptureHistoryItemTypeClaimStatusUpdatedComponent,
              CaptureHistoryItemTypeClientNoteAddedComponent,
              CaptureHistoryItemTypeClientNoteRemovedComponent,
              MatProgressSpinner,
              DatePipe]
})
export class CaptureHistoryNewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private captureService = inject(CaptureService);
  private router = inject(Router);
  private titleService = inject(Title);
  private userPresenceService = inject(UserPresenceService);

  captureId: string;
  capture: Capture;
  captureHistoryItems: CaptureHistoryItem[] = [];
  captureHistoryItemActions = CaptureHistoryItemActions;

  loadingCapture = false;
  loadingHistoryItems = false;

  captureChangedSubscription = null;

  ngOnInit() {
    this.captureId = this.route.snapshot.paramMap.get('id');

    this.loadCapture(this.captureId);
    this.loadHistoryItems(this.captureId);

    this.captureChangedSubscription = this.captureService.captureChanged.subscribe(({ capture }) => {
      // is it our capture?
      if (capture.id === this.capture.id) {
        this.capture = capture;
        this.loadHistoryItems(capture.id);
      }
    });

    this.userPresenceService.subscribeAndJoin("capture", this.captureId)
  }

  ngOnDestroy() {
    if (this.captureChangedSubscription) {
      this.captureChangedSubscription.unsubscribe();
    }

    this.userPresenceService.leaveAndUnsubscribe("capture", this.captureId);
  }

  private loadCapture(captureId): void {
    this.loadingCapture = true;
    this.captureService.getCapture(captureId).subscribe(
      capture => {
        this.capture = capture;
        this.loadingCapture = false;
        this.setTitle();
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
        }
      }
    );
  }

  private loadHistoryItems(captureId): void {
    this.loadingHistoryItems = true;
    this.captureService.getCaptureHistoryItems(captureId).subscribe(
      captureHistoryItems => {
        this.loadingHistoryItems = false;
        this.captureHistoryItems = captureHistoryItems.reverse();
      },
      err => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
        }
      }
    );
  }

  private setTitle() {
    const title = `R1 340B Recovery - ${this.capture.candidate.claimIdentifier} | History`;
    this.titleService.setTitle(title);
  }
}
